/**
 * Document vault. Upload PDF/image files, encrypt blob, store in OPFS,
 * keep metadata in vault. Camera-capture path included.
 */
import { store } from '../store.ts';
import { withVault, injectCommonStyles, escapeHtml } from './_shared.ts';
import { newId, DOCUMENT_CATEGORIES } from '../types.ts';
import type { BlobId, DocumentCategory, DocumentEntry, DocumentId } from '../types.ts';
import { saveBlob, deleteBlob, storageEstimate } from '../chassis/storage.ts';
import { capturePhoto, isCameraSupported } from '../chassis/camera.ts';

const TPL = /* html */ `
<style>
.usage {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 8px;
  font-family: var(--mono);
  font-size: 10px;
  letter-spacing: 0.2em;
  color: var(--ink-faint);
  text-transform: uppercase;
  margin-bottom: 12px;
}
input[type='file'] { padding: 8px; background: var(--parchment); }
.actions { display: flex; gap: 8px; flex-wrap: wrap; }
</style>
<section class="frame">
  <h2>Document vault <span class="deva">दस्तावेज़</span></h2>
  <div class="kicker">Will · marriage cert · property deeds · insurance · medical</div>
  <div class="usage">
    <span>storage used</span>
    <span id="usage">— / —</span>
  </div>
  <div class="list" id="list"></div>
  <form class="form-grid" id="form">
    <div class="row">
      <label class="field">Category
        <select id="cat" required>
          ${DOCUMENT_CATEGORIES.map((c) => `<option value="${c}">${c.replace(/_/g, ' ')}</option>`).join('')}
        </select>
      </label>
      <label class="field">Title
        <input id="title" type="text" required placeholder="e.g. Marriage certificate 2018" />
      </label>
    </div>
    <label class="field">Upload (encrypted to OPFS, never uploaded)
      <input id="file" type="file" accept="application/pdf,image/*" />
    </label>
    <label class="field">Notes
      <input id="notes" type="text" placeholder="e.g. original with mom at her house" />
    </label>
    <div class="actions">
      <button type="submit" class="btn">Add</button>
      <button type="button" class="btn ghost" id="camera-btn">Use camera</button>
      <button type="reset" class="btn ghost">Reset</button>
    </div>
  </form>
</section>
`;

export class WasiyatDocumentVault extends HTMLElement {
  #shadow = this.attachShadow({ mode: 'open' });
  #unsub: (() => void) | null = null;
  #pendingBytes: Uint8Array | null = null;
  #pendingMime = 'application/pdf';

  connectedCallback(): void {
    injectCommonStyles(this.#shadow);
    this.#shadow.innerHTML += TPL;
    this.#unsub = withVault(this, (v, root) => {
      const list = root.getElementById('list');
      if (!list) return;
      if (v.documents.length === 0) {
        list.innerHTML = `<div class="list-empty">No documents indexed yet.</div>`;
      } else {
        list.innerHTML = v.documents.map((d) => `<div class="list-row">
          <div>
            <div class="title">${escapeHtml(d.title)}</div>
            <div class="sub">${escapeHtml(d.category.replace(/_/g, ' '))} · ${d.size_kb} KB · ${escapeHtml(d.mime_type)}${d.notes ? ' · ' + escapeHtml(d.notes.slice(0, 80)) : ''}</div>
          </div>
          <button class="btn tiny ghost" data-del="${d.id}" data-blob="${d.blob_id ?? ''}">Remove</button>
        </div>`).join('');
        list.querySelectorAll<HTMLButtonElement>('button[data-del]').forEach((b) => {
          b.addEventListener('click', async () => {
            const id = b.dataset['del'] as DocumentId;
            const blob = b.dataset['blob'];
            if (blob) await deleteBlob(blob as BlobId);
            void store.mutate((draft) => { draft.documents = draft.documents.filter((x) => x.id !== id); });
          });
        });
      }
    });

    void this.#refreshUsage();

    const fileInput = this.#shadow.getElementById('file') as HTMLInputElement;
    fileInput.addEventListener('change', async () => {
      const f = fileInput.files?.[0];
      if (!f) { this.#pendingBytes = null; return; }
      this.#pendingBytes = new Uint8Array(await f.arrayBuffer());
      this.#pendingMime = f.type || 'application/pdf';
    });

    const camBtn = this.#shadow.getElementById('camera-btn') as HTMLButtonElement;
    if (!isCameraSupported()) camBtn.style.display = 'none';
    camBtn.addEventListener('click', async () => {
      const res = await capturePhoto();
      if (!res) {
        camBtn.textContent = 'Camera unavailable';
        return;
      }
      this.#pendingBytes = new Uint8Array(await res.blob.arrayBuffer());
      this.#pendingMime = res.mime;
      camBtn.textContent = `Captured ${(this.#pendingBytes.byteLength / 1024).toFixed(1)} KB`;
    });

    const form = this.#shadow.getElementById('form') as HTMLFormElement;
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const cat = (this.#shadow.getElementById('cat') as HTMLSelectElement).value as DocumentCategory;
      const title = (this.#shadow.getElementById('title') as HTMLInputElement).value.trim();
      const notes = (this.#shadow.getElementById('notes') as HTMLInputElement).value.trim();
      if (!title) return;
      let blobId: BlobId | null = null;
      let sizeKb = 0;
      if (this.#pendingBytes) {
        blobId = newId<BlobId>();
        await saveBlob(blobId, this.#pendingBytes);
        sizeKb = Math.round(this.#pendingBytes.byteLength / 1024);
      }
      const d: DocumentEntry = {
        id: newId<DocumentId>(),
        category: cat,
        title,
        blob_id: blobId,
        size_kb: sizeKb,
        mime_type: this.#pendingMime,
        notes,
        password_protected: false,
        created_at: new Date().toISOString(),
      };
      await store.mutate((draft) => { draft.documents.push(d); });
      this.#pendingBytes = null;
      form.reset();
      camBtn.textContent = 'Use camera';
      void this.#refreshUsage();
    });
  }

  disconnectedCallback(): void { this.#unsub?.(); }

  async #refreshUsage(): Promise<void> {
    const u = this.#shadow.getElementById('usage');
    if (!u) return;
    const est = await storageEstimate();
    u.textContent = est ? `${est.usage_mb} MB / ${est.quota_mb} MB` : 'OPFS unavailable';
  }
}

customElements.define('wasiyat-document-vault', WasiyatDocumentVault);
